import { all, call, put, takeLatest } from 'redux-saga/effects';

import * as errorHandlerActions from '@/components/errorHandler/Actions';
import * as dailyGoalActions from '@/pages/dailyGoal/Actions';
import * as sharedActions from '@/shared/Actions';
import { dailyGoalActionTypes } from '@/pages/dailyGoal/Constants';
import { getCollectRewardPath, getUpdateRewardPath, ROUTES_API } from '@/services/Constants';
import { processRequest } from '@/services/Api';

export default function* () {
	yield all([
		yield takeLatest(
			dailyGoalActionTypes.GET_DAILY_GOAL_REPORT_REQUEST,
			handleGetDailyGoalReportRequest,
		),
		yield takeLatest(
			dailyGoalActionTypes.GET_DAILY_GOAL_REFLECTIONS_REQUEST,
			handleGetDailyGoalReflectionsRequest,
		),
		yield takeLatest(
			dailyGoalActionTypes.SEND_DAILY_GOAL_REFLECTION_REQUEST,
			handleSendDailyGoalReflectionRequest,
		),
		yield takeLatest(
			dailyGoalActionTypes.GET_DAILY_GOAL_STICKERS_REQUEST,
			handleGetDailyGoalStickersRequest,
		),
		yield takeLatest(
			dailyGoalActionTypes.SEND_DAILY_GOAL_STICKER_REQUEST,
			handleSendDailyGoalStickerRequest,
		),
		yield takeLatest(
			dailyGoalActionTypes.GET_USER_STICKERS_REQUEST,
			handleGetUserStickersRequest,
		),
		yield takeLatest(
			dailyGoalActionTypes.GET_PROFILE_STICKERS_REQUEST,
			handleGetProfileStickersRequest,
		),
		yield takeLatest(dailyGoalActionTypes.COLLECT_REWARD_REQUEST, handleCollectRewardRequest),
		yield takeLatest(dailyGoalActionTypes.UPDATE_REWARD_REQUEST, handleUpdateRewardRequest),
		yield takeLatest(
			dailyGoalActionTypes.GET_DAILY_GOAL_SUMMARY_REQUEST,
			handleGetDailyGoalSummaryRequest,
		),
		yield takeLatest(
			dailyGoalActionTypes.GET_WEEK_GOAL_TILES_REQUEST,
			handleGetWeekGoalTilesRequest,
		),
	]);
}

export function* handleGetDailyGoalReportRequest() {
	try {
		const { data } = yield call(processRequest, ROUTES_API.DAILY_REPORT);

		yield put(dailyGoalActions.getDailyGoalReportSuccess(data));
	} catch (e) {
		yield put(dailyGoalActions.getDailyGoalReportError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleGetDailyGoalReflectionsRequest() {
	try {
		const { data } = yield call(processRequest, ROUTES_API.USER_GOAL_REFLECTIONS_VALUATIONS);

		yield put(dailyGoalActions.getDailyGoalReflectionsSuccess(data));
	} catch (e) {
		yield put(dailyGoalActions.getDailyGoalReflectionsError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleSendDailyGoalReflectionRequest({ payload }) {
	try {
		const { value } = payload || {};
		const body = { user_goal_reflection: { valuation: value } };

		yield call(processRequest, ROUTES_API.USER_GOAL_REFLECTIONS, 'POST', body);

		yield put(dailyGoalActions.sendDailyGoalReflectionSuccess());
	} catch (e) {
		yield put(dailyGoalActions.sendDailyGoalReflectionError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleGetDailyGoalStickersRequest() {
	try {
		const { data } = yield call(processRequest, ROUTES_API.DAYGOAL_STICKERS_LIST);

		yield put(dailyGoalActions.getDailyGoalStickersSuccess(data));
	} catch (e) {
		yield put(dailyGoalActions.getDailyGoalStickersError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleSendDailyGoalStickerRequest({ payload }) {
	try {
		const { sticker } = payload || {};

		const { data } = yield call(processRequest, ROUTES_API.USER_STICKERS, 'POST', sticker);

		yield put(dailyGoalActions.sendDailyGoalStickerSuccess(data));
	} catch (e) {
		yield put(dailyGoalActions.sendDailyGoalStickerError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleGetUserStickersRequest() {
	try {
		const { data } = yield call(processRequest, ROUTES_API.USER_STICKERS);

		yield put(dailyGoalActions.getUserStickersSuccess(data));
	} catch (e) {
		yield put(dailyGoalActions.getUserStickersError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleGetProfileStickersRequest() {
	try {
		const { data } = yield call(processRequest, ROUTES_API.USER_STICKERS_LIST);

		yield put(dailyGoalActions.getProfileStickersSuccess(data));
	} catch (e) {
		yield put(dailyGoalActions.getProfileStickersError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleCollectRewardRequest({ payload }) {
	try {
		const { id, body } = payload || {};

		const { data } = yield call(processRequest, getCollectRewardPath(id), 'POST', body);

		const { data: userData } = yield call(processRequest, ROUTES_API.GET_USER_DATA);

		yield put(sharedActions.getUserDataSuccess(userData));

		yield put(dailyGoalActions.collectRewardSuccess(data));
	} catch (e) {
		yield put(dailyGoalActions.collectRewardError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleUpdateRewardRequest({ payload }) {
	try {
		const { id, body, callback } = payload || {};

		yield call(processRequest, getUpdateRewardPath(id), 'PUT', body);

		const { data: userData } = yield call(processRequest, ROUTES_API.GET_USER_DATA);

		yield put(sharedActions.getUserDataSuccess(userData));

		callback && callback();

		yield put(dailyGoalActions.updateRewardSuccess());
	} catch (e) {
		yield put(dailyGoalActions.updateRewardError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleGetDailyGoalSummaryRequest() {
	try {
		const { data } = yield call(processRequest, ROUTES_API.DAYGOAL_SUMMARY, 'GET');
		const { progress, current_week_goal, day_goal_count } = data || {};

		yield put(
			dailyGoalActions.getDailyGoalSummarySuccess(
				progress,
				current_week_goal,
				day_goal_count,
			),
		);
	} catch (e) {
		yield put(dailyGoalActions.getDailyGoalSummaryError(e));
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleGetWeekGoalTilesRequest() {
	try {
		const { data } = yield call(processRequest, ROUTES_API.WEEK_GOAL_LIST);

		yield put(dailyGoalActions.getWeekGoalTilesSuccess(data));
	} catch (e) {
		yield put(dailyGoalActions.getWeekGoalTilesError(e));
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}
